import { useCallback } from 'react';
import { useEnabledHiddenFields as useHiddenFieldsByUim } from '@atlassian/ui-modifications-core/controllers';
import type { ContainersByType } from '@atlassian/jira-issue-layout-common-constants/src/index.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useUserPreferencesValue } from '@atlassian/jira-issue-user-preference-services/src/main.tsx';
import {
	useIsFullPageIssueView,
	useIssueKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import {
	SIDE_PANEL_EXPERIMENT_ISSUE_ACTIVITY_LAYOUT,
	USER_LAYOUT_PREFERENCE,
} from '@atlassian/jira-issue-shared-types/src/common/types/user-preferences-type.tsx';
import {
	useProjectKey as useProjectKeyNew,
	useProjectType as useProjectTypeNew,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	SOFTWARE_PROJECT,
	CORE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import isMobileUserAgent from '@atlassian/jira-common-is-mobile-user-agent/src/index.tsx';
import {
	useIssueLayoutActionsStore,
	useIssueLayoutGlanceStore,
	useIssueLayoutContextPanelStore,
	useIssueLayoutContainersStore,
} from './context.tsx';
import type { Glance, ContextPanelItem } from './types.tsx';
import { filterOutItemsWithProvidedIds } from './utils.tsx';

export const useIssueLayoutContainers = (issueKey: IssueKey) => {
	const [containers] = useIssueLayoutContainersStore(issueKey);
	return [containers] as const;
};

export const useLayoutContainerByType = (issueKey: IssueKey, type: string) => {
	const [containers] = useIssueLayoutContainersStore(issueKey);
	const container = containers.find((innerContainer) => innerContainer.containerType === type);
	// Forge app with UI Modifications module can hide selected fields using
	// UI modifications Field API. We are removing them here from the layout fields
	const fieldsHiddenByUim = useHiddenFieldsByUim();

	return [filterOutItemsWithProvidedIds(container?.items.nodes ?? [], fieldsHiddenByUim)] as const;
};

export const useIssueLayoutGlance = (issueKey: IssueKey) => {
	const [glance] = useIssueLayoutGlanceStore(issueKey);

	return [glance] as const;
};

export const useIssueLayoutContextPanel = (issueKey: IssueKey) => {
	const [contextPanel] = useIssueLayoutContextPanelStore(issueKey);

	return [contextPanel] as const;
};

export const useUserPreferredWorkItemLayout = () => {
	try {
		if (!fg('issue-view-side-panel-activity-kill-switch')) {
			return {
				isInDiscussionLayoutExperiment: false,
				isInWideLayoutExperiment: false,
				isActivityInVerticalSidePanel: false,
				userPreferredLayout: null,
			};
		}
		/* eslint-disable react-hooks/rules-of-hooks */
		const issueKey = useIssueKey();
		const projectKey = useProjectKeyNew(issueKey);
		const projectType = useProjectTypeNew(projectKey);
		const isFullIssueView = useIsFullPageIssueView();
		const isMobile = isMobileUserAgent();
		const isSoftwareOrBusiness = projectType === SOFTWARE_PROJECT || projectType === CORE_PROJECT;

		const isInWideLayoutExperiment =
			!isMobile &&
			isSoftwareOrBusiness &&
			isFullIssueView &&
			expVal('wide_issue_layout_in_issue_view', 'is_enabled', false);

		const isInDiscussionLayoutExperiment =
			!isMobile &&
			isSoftwareOrBusiness &&
			isFullIssueView &&
			expVal('issue-view-side-panel-activity', 'isActivityInSidePanel', false);

		const [deprecatedUserPreferredLayout] = useUserPreferencesValue(
			SIDE_PANEL_EXPERIMENT_ISSUE_ACTIVITY_LAYOUT,
		);
		const [userPreferredLayout] = useUserPreferencesValue(USER_LAYOUT_PREFERENCE);

		const userLayoutType =
			isInWideLayoutExperiment && userPreferredLayout && typeof userPreferredLayout === 'string'
				? JSON.parse(userPreferredLayout)?.layoutType
				: null;
		/* eslint-enable react-hooks/rules-of-hooks */

		const userSelectedLayout = isInWideLayoutExperiment // Use new value if wide layout experiment is enabled
			? userLayoutType
			: deprecatedUserPreferredLayout;

		// isActivityInVerticalSidePanel is not needed and can be derived from userSelectedLayout
		// but we keep it to avoid making too many changes in the codebase
		const isActivityInVerticalSidePanel =
			isInDiscussionLayoutExperiment &&
			(userSelectedLayout === 'VERTICAL' || userSelectedLayout === 'DISCUSSION');

		return {
			isInDiscussionLayoutExperiment: Boolean(isInDiscussionLayoutExperiment),
			isInWideLayoutExperiment: Boolean(isInWideLayoutExperiment),
			// isActivityInVerticalSidePanel is not needed and can be derived from userSelectedLayout
			// but we keep it to avoid making too many changes in the codebase
			isActivityInVerticalSidePanel: Boolean(isActivityInVerticalSidePanel),
			userPreferredLayout: userSelectedLayout,
		};
	} catch (error) {
		log.safeErrorWithoutCustomerData(
			'issue-view-layout.services.main',
			`Error in useUserPreferredWorkItemLayout : ${error}`,
		);
		return {
			isInDiscussionLayoutExperiment: false,
			isInWideLayoutExperiment: false,
			isActivityInVerticalSidePanel: false,
			userPreferredLayout: null,
		};
	}
};

export const useIssueLayoutActions = () => {
	const [, actions] = useIssueLayoutActionsStore();

	const setIssueViewContainersLayout = useCallback(
		(issueKey: IssueKey, containersByType: ContainersByType) => {
			actions.setIssueViewContainersLayout(issueKey, containersByType);
		},
		[actions],
	);

	const setIssueViewLayoutGlance = useCallback(
		(issueKey: IssueKey, glance?: Glance) => {
			actions.setIssueViewLayoutGlance(issueKey, glance);
		},
		[actions],
	);

	const setIssueViewLayoutContextPanel = useCallback(
		(issueKey: IssueKey, newContextPanel?: ContextPanelItem) => {
			actions.setIssueViewLayoutContextPanel(issueKey, newContextPanel);
		},
		[actions],
	);

	return [
		null,
		{
			setIssueViewContainersLayout,
			setIssueViewLayoutGlance,
			setIssueViewLayoutContextPanel,
		},
	] as const;
};
